import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import type { ComponentProps, FC } from 'react';

export interface RatingProps extends ComponentProps<'div'> {
   value?: number;
   size?: RatingStarSize;
   ariaLabel: string;
}

const stars = Array.from({ length: 5 }, (_, index) => index + 1);

export const Rating: FC<RatingProps> = (props: RatingProps) => {
   const { value = 5, ariaLabel, size, className, ...rest } = props;

   const halfStarsValue = Math.round(value * 2);

   return (
      <div
         className={cn('flex items-center gap-1', className)}
         {...rest}
         aria-label={ariaLabel}
         role="img"
      >
         {stars.map(i => {
            let appearance = RatingStarAppearance.Empty;
            if (halfStarsValue >= i * 2) {
               appearance = RatingStarAppearance.Full;
            } else if (halfStarsValue >= i * 2 - 1) {
               appearance = RatingStarAppearance.Half;
            }
            return <RatingStar key={i} size={size} appearance={appearance} />;
         })}
      </div>
   );
};

// The sprite has no intrinsic size and the icon base style pins the width to 1em,
// so w-auto plus the star's 576x512 viewBox ratio sets it from the height.
const starClassName = 'w-auto aspect-[576/512]';
const starHeightClassName = {
   3: 'h-3',
   4: 'h-4',
   5: 'h-5',
} as const;

type RatingStarSize = keyof typeof starHeightClassName;

interface RatingStarProps {
   appearance: RatingStarAppearance;
   size?: RatingStarSize;
}

export enum RatingStarAppearance {
   Full = 'full',
   Half = 'half',
   Empty = 'empty',
}

const RatingStar = ({ appearance = RatingStarAppearance.Empty, size = 4 }: RatingStarProps) => {
   const heightClassName = starHeightClassName[size];
   switch (appearance) {
      case RatingStarAppearance.Empty: {
         return (
            <FaIcon name="star" className={cn(starClassName, heightClassName, 'text-gray-300')} />
         );
      }
      case RatingStarAppearance.Full: {
         return (
            <FaIcon name="star" className={cn(starClassName, heightClassName, 'text-brand-500')} />
         );
      }
      case RatingStarAppearance.Half: {
         return (
            <FaIcon
               name="star-half"
               family="duotone"
               className={cn(
                  starClassName,
                  heightClassName,
                  'text-brand-500 [--fa-secondary-fill:var(--color-gray-300)] [--fa-secondary-opacity:1]'
               )}
            />
         );
      }
   }
};
